const _ = require("lodash");
import {
  RecipeShoppingListEntry,
  ManualShoppingListEntry,
} from "@/models/ShoppingListEntry.ts";
import { Recipe } from "@/models/Recipe";
import { Ingredient } from "@/models/Ingredient";
import { Shop } from "@/models/Shop";

export default {
  saveDbUrl(state: any, dbUrl: string) {
    state.dbUrl = dbUrl;
    localStorage.setItem("dbUrl", state.dbUrl);
  },
  savePreferences(state: any, preferences: object) {
    state.preferences = _.defaults(preferences, state.preferences);
    localStorage.setItem("preferences", JSON.stringify(state.preferences));
  },
  saveLatestRoute(state: any, route: string) {
    state.latestRoute = route;
    localStorage.setItem("latestRoute", route);
  },
  addDismissedTutorialBoxId(state: any, id: string) {
    state.dismissedTutorialBoxIds.push(id);
    localStorage.setItem(
      "dismissedTutorialBoxIds",
      JSON.stringify(state.dismissedTutorialBoxIds)
    );
  },
  initialReplicationStarted(state: any) {
    state.initialReplicationIsInProgress = true;
  },
  initialReplicationStopped(state: any) {
    state.initialReplicationIsInProgress = false;
  },
  initialReplicationUpdatePending(state: any, pendingCount: number) {
    if (state.initialReplicationPendingMaxCount === null) {
      state.initialReplicationPendingMaxCount = pendingCount;
    }
    state.initialReplicationPendingCount = pendingCount;
  },
  performanceMeasurementStart(state: any, name: string) {
    state.performanceMeasurementsRunning[name] = performance.now();
  },
  performanceMeasurementFinish(state: any, name: string) {
    const measurement = {
      name: name,
      duration: performance.now() - state.performanceMeasurementsRunning[name],
    };
    state.performanceMeasurementsFinished.push(measurement);

    /* istanbul ignore next */
    if (
      localStorage.getItem("preferences") &&
      JSON.parse(localStorage.getItem("preferences")).sentryErrorTrackingOptIn
    ) {
      var honeycombWriteKey = "2b0cd9f985948dee74456d0425b1ee3f";
      var honeycombDatasetName = "Meal Planner";
      var honeycombEvent = {
        type: "performanceMeasurement",
        action: measurement.name,
        duration: measurement.duration,
        appRelease: process.env.VUE_APP_RELEASE_STRING || "dev",
        userAgent: navigator.userAgent || "unknown",
        historicEntryCount: state.shoppingList.historicEntries.length,
        hasSyncUrl: !!state.dbUrl,
        isInitializedFromPersistance: state.isInitializedFromPersistance,
        initialReplicationIsInProgress: state.initialReplicationIsInProgress,
      };

      var xhr = new XMLHttpRequest();
      xhr.open(
        "POST",
        "https://api.honeycomb.io/1/events/" +
          encodeURIComponent(honeycombDatasetName),
        true
      );
      xhr.setRequestHeader("X-Honeycomb-Team", honeycombWriteKey);
      xhr.send(JSON.stringify(honeycombEvent));
    }

    delete state.performanceMeasurementsRunning[name];
  },
  refreshEntriesFromDbData(state: any, dbResult: any) {
    const docs = dbResult.rows.map((row: any) => row.doc);

    const recipeShoppingListEntries = docs
      .filter((doc: { _id: string }) =>
        _.startsWith(doc._id, "RecipeShoppingListEntry:")
      )
      .map((entryData: any) => {
        return RecipeShoppingListEntry.fromPureObject(entryData);
      });

    const manualShoppingListEntries = docs
      .filter((doc: { _id: string }) =>
        _.startsWith(doc._id, "ManualShoppingListEntry:")
      )
      .map((entryData: any) => {
        try {
          return ManualShoppingListEntry.fromPureObject(entryData);
        } catch (e) {
          console.info(
            "Skipping incomplete ManualShoppingListEntry",
            entryData
          );
          // throw e;
          return null;
        }
      })
      .filter((entry: ManualShoppingListEntry | null) => entry);

    state.shoppingList.entries = recipeShoppingListEntries.concat(
      manualShoppingListEntries
    );
  },
  refreshArchivedsFromDbData(state: any, dbResult: any) {
    const docs = dbResult.rows.map((row: any) => row.doc);
    const historicShoppingListEntries = docs
      .filter((doc: { _id: string }) => {
        return _.startsWith(doc._id, "Archived:");
      })
      .map((entryData: any) => {
        if (_.includes(entryData._id, "RecipeShoppingListEntry:")) {
          return RecipeShoppingListEntry.fromPureObject(entryData);
        }

        if (_.includes(entryData._id, "ManualShoppingListEntry:")) {
          return ManualShoppingListEntry.fromPureObject(entryData);
        }
      });

    state.shoppingList.historicEntries = historicShoppingListEntries;
  },
  refreshMealsFromDbData(state: any, dbResult: any) {
    const docs = dbResult.rows.map((row: any) => row.doc);
    state.meals = docs.filter(
      (doc: { _id: string }) => doc._id.indexOf("Meal:") > -1
    );
  },
  refreshRecipesFromDbData(state: any, dbResult: any) {
    const docs = dbResult.rows.map((row: any) => row.doc);
    state.recipes = docs
      .filter((doc: { _id: string }) => _.startsWith(doc._id, "Recipe:"))
      .map(Recipe.fromPureObject);

    if (state.recipes.length === 0) {
      let exampleRecipe = new Recipe({ name: "Pancakes" });
      exampleRecipe.ingredients = [
        new Ingredient("250g Flour"),
        new Ingredient("250ml Milk"),
        new Ingredient("2 Eggs"),
        new Ingredient("Baking soda"),
      ];
      state.recipes.push(exampleRecipe);
    }
  },
  refreshShopsFromDbData(state: any, dbResult: any) {
    const docs = dbResult.rows.map((row: any) => row.doc);

    state.shops = docs
      .filter((doc: { _id: string }) => _.startsWith(doc._id, "Shop:"))
      .map((doc: any) => {
        return new Shop(doc);
      });
  },
  finishedInitializationFromPersistance(state: any) {
    state.isInitializedFromPersistance = true;
  },
  loadDemoState(state: any, demoState: any) {
    _.merge(state, demoState);
  },
};
